import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Skeleton, Row, Col, Layout, Grid } from 'antd';
import Bugsnag from '@bugsnag/js';
import { useTranslation } from 'react-i18next';

import RecentQueries from '../components/query/RecentQueries';
import QueryDetailsCard from '../components/query/QueryDetailsCard';
import usePageMeta from '../hooks/usePageMeta';
import BackLink from '../components/BackLink';

import { handleError } from '../utils/errorHandling';
import { debounce } from 'lodash';
import QuerySearchAutocomplete from "../components/query/QuerySearchAutocomplete";
import useQueryPolling from '../hooks/useQueryPolling';

const { Content } = Layout;
const { useBreakpoint } = Grid;

const QueryDetails = () => {
  const { uuid } = useParams();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [reloadTrigger, setReloadTrigger] = useState(0);
  
  const { query, loading, error } = useQueryPolling(uuid);

  // Handle reload of recent queries
  const handleReloadRecentQueries = useCallback(
    debounce(() => {
      setReloadTrigger((prev) => prev + 1);
    }, 500),
    []
  );

  // Use your page meta hook (sets <title>, etc.)
  usePageMeta(query?.getQuestion?.());

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error('[QueryDetails] Query error:', {
        uuid,
        error: error.message || error
      });
      
      handleError(error);
    }
  }, [error, uuid]);

  // Clean up effect
  useEffect(() => {
    return () => {
      console.log('[QueryDetails] Cleanup', { uuid });
    };
  }, [uuid]);

  // Loading case
  if (loading && !query) {
    console.log('[QueryDetails] State: Loading query', { uuid });
    return (
      <Content className="page">
        <Skeleton active />
      </Content>
    );
  }

  // No query case
  if (!query) {
    console.log('[QueryDetails] Rendering empty state');
    return <Content className="page" />;
  }

  // Main content render
  console.log('[QueryDetails] Rendering main content:', {
    uuid: query.getUUID?.(),
    isCompleted: query.isCompleted?.()
  });

  return (
    <Content className="page">
      <Row gutter={[32, 32]}>
        {/* Mobile Back Link */}
        <Col xs={24} md={0}>
          <BackLink to='/queries/new' preventHistory={true} />
        </Col>
        
        {/* Sidebar: Search + Recent Queries */}
        <Col xs={24} md={8} lg={8}>
          <QuerySearchAutocomplete/>
          <RecentQueries
            activeQueryUuid={uuid}
            reloadTrigger={reloadTrigger}
          />
        </Col>

        {/* Query Details Card */}
        <Col xs={24} md={16} lg={16}>
          <QueryDetailsCard
            query={query}
            onReloadRecentQueries={handleReloadRecentQueries}
          />
        </Col>
      </Row>
    </Content>
  );
};

export default QueryDetails;
